// Mobile full-screen Direct Messages overlay — the small-screen sibling of DmSidebar.
// Structural subsets of ChatLayout's types — only what this overlay renders.
type Space = { id: string; name?: string | null; avatarUrl?: string | null };
type DmMsg = {
  createdAt?: string;
  authorId?: string | number;
  authorName?: string;
  kind?: string;
  attachments?: unknown[];
  content?: string;
};
type DmFriend = { id: string | number; name?: string; username?: string; avatarUrl?: string | null };
type Me = { userId?: string; name?: string | null; username?: string | null; avatarUrl?: string | null };

type MobileDmPageProps = {
  // Top bar
  spaces: Space[];
  hiddenSpaces: Record<string, boolean>;
  currentVoidId: string;
  unread: Record<string, number>;
  me: Me;
  userQuickOpen: boolean;
  onToggleQuickSettings: () => void;
  onClose: () => void;
  onGoLanding: () => void;
  onSwitchSpace: (spaceId: string) => void;
  // Search + friends + DM list
  dmSearch: string;
  dmSearchOpen: boolean;
  setDmSearch: (value: string) => void;
  setDmSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  friendsOnline: DmFriend[];
  friendsList: DmFriend[];
  dmStartBusyUserId: string | null;
  activeDmVoids: Space[];
  msgsByKey: Record<string, DmMsg[]>;
  notified: Record<string, boolean>;
  tabBgClass: string;
  getDmDisplayName: (space?: { id?: string; name?: string | null } | null, fallbackId?: string) => string;
  onStartDm: (userId: string) => void | Promise<void>;
  onOpenFriends: () => void;
  onOpenDm: (voidId: string) => void;
};

function MobileDmPage({
  spaces,
  hiddenSpaces,
  currentVoidId,
  unread,
  me,
  userQuickOpen,
  onToggleQuickSettings,
  onClose,
  onGoLanding,
  onSwitchSpace,
  dmSearch,
  dmSearchOpen,
  setDmSearch,
  setDmSearchOpen,
  friendsOnline,
  friendsList,
  dmStartBusyUserId,
  activeDmVoids,
  msgsByKey,
  notified,
  tabBgClass,
  getDmDisplayName,
  onStartDm,
  onOpenFriends,
  onOpenDm,
}: MobileDmPageProps) {
  return (
    <div className="md:hidden fixed inset-0 z-50 flex flex-col brand-app-bg text-fg">
      {/* Top bar — identical markup to the main mobile header for seamless appearance */}
      <div
        className="mobile-chrome fixed top-0 left-0 right-0 z-10 w-full"
        style={{ transform: "translateY(var(--vv-top, 0px))" }}
      >
        <div className="px-4 pt-[calc(env(safe-area-inset-top)+8px)] pb-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            {/* Left: home + DM buttons (same as regular header; DM button active) */}
            <div className="flex items-center gap-2">
              <button
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-fg-2 hover:text-white"
                onClick={onGoLanding}
                title="Go to landing"
                aria-label="Go to landing"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M3 9l9-7 9 7"/><path d="M9 22V12h6v10"/></svg>
              </button>
              <button
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-emerald-400 text-white"
                onClick={onClose}
                title="Close Direct Messages"
                aria-label="Close Direct Messages"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>
              </button>
            </div>
            {/* Center: space icons */}
            <div className="min-w-0 text-center">
              <div className="overflow-x-auto flex items-center justify-center gap-2 px-1.5 py-1.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {spaces.filter(v => !String(v.id).startsWith('dm_') && !hiddenSpaces[v.id]).map(v => {
                  const active = v.id === currentVoidId;
                  const spaceUnread = Object.keys(unread).filter(k => k.startsWith(`${v.id}:`)).reduce((s, k) => s + (unread[k] || 0), 0);
                  return (
                    <button key={v.id} className="relative shrink-0" onClick={() => onSwitchSpace(v.id)}>
                      <div className={`h-8 w-8 rounded-full border ${active ? 'border-emerald-400 ring-2 ring-emerald-500/30' : 'border-white/15'} bg-white/5 flex items-center justify-center overflow-hidden`}>
                        {v.avatarUrl ? <ThumbImg w={56} src={v.avatarUrl} alt={v.name || ''} className="h-full w-full object-cover" /> : <span className="text-meta font-medium">{(v.name?.[0] || '?').toUpperCase()}</span>}
                      </div>
                      {spaceUnread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-emerald-500 ring-2 ring-neutral-950 text-white text-[0.5625rem] font-semibold flex items-center justify-center leading-none">{spaceUnread > 99 ? '99+' : spaceUnread}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Right: user avatar — same button as regular header */}
            <button
              className={`relative shrink-0 h-9 w-9 rounded-full border ${userQuickOpen ? 'border-emerald-500 shadow-[0_8px_24px_rgba(16,185,129,0.35)]' : 'border-white/15'} bg-black/40 overflow-hidden flex items-center justify-center`}
              title="Quick settings"
              aria-label="Quick settings"
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); onToggleQuickSettings(); }}
            >
              {me.avatarUrl
                ? <ThumbImg w={56} src={me.avatarUrl} alt={me.name || 'me'} className="h-full w-full object-cover" />
                : <span className="text-xs font-medium">{(me.name || me.username || '?')[0].toUpperCase()}</span>
              }
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable body — padded to clear the fixed top bar */}
      <div className="flex-1 overflow-y-auto" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 72px)' }}>
        {/* Page title + search + add friends */}
        <div className="px-4 pt-4 pb-2">
          <h1 className="text-2xl font-medium text-white mb-3">Messages</h1>
          <div className="flex items-center gap-2">
            {/* Search icon button — expands inline when active */}
            <button
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-white/5 transition-colors ${dmSearchOpen ? 'border-emerald-500/60 text-white' : 'border-white/15 text-fg-2 hover:text-white'}`}
              aria-label="Search"
              onClick={() => { setDmSearchOpen(o => !o); setDmSearch(''); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </button>
            {/* Add Friends button */}
            <button
              className="flex-1 flex items-center justify-center gap-2 h-10 rounded-full border border-white/15 bg-white/5 text-sm font-medium text-fg hover:text-white hover:border-white/25 active:bg-white/10 transition-colors"
              onClick={onOpenFriends}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
              Add Friends
            </button>
          </div>
          {/* Inline search input — shown when search is toggled open */}
          {dmSearchOpen && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-white/8 border border-white/10 px-3 py-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-fg-3 shrink-0"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                autoFocus
                value={dmSearch}
                onChange={e => setDmSearch(e.target.value)}
                placeholder="Search messages..."
                aria-label="Search messages"
                className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-fg-3"
              />
              <button className="text-fg-3 hover:text-white" onClick={() => { setDmSearch(''); setDmSearchOpen(false); }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          )}
        </div>

        {/* Online friends shortcuts */}
        {!dmSearchOpen && friendsOnline.length > 0 && (
          <div className="px-4 pb-2">
            <div className="flex gap-4 overflow-x-auto py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {friendsOnline.map(f => (
                <button
                  key={f.id}
                  className="flex flex-col items-center gap-1.5 shrink-0"
                  disabled={!!dmStartBusyUserId}
                  onClick={() => { void onStartDm(String(f.id)); }}
                >
                  <div className="relative h-14 w-14">
                    <div className="h-full w-full rounded-full overflow-hidden bg-white/10 flex items-center justify-center border-2 border-online/60">
                      {f.avatarUrl
                        ? <ThumbImg w={56} src={f.avatarUrl} alt={f.name || f.username || ''} className="h-full w-full object-cover" />
                        : <span className="text-base font-medium text-fg-2">{((f.name || f.username || '?')[0]).toUpperCase()}</span>
                      }
                    </div>
                    <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-online ring-2 ring-black" />
                  </div>
                  <span className="text-meta text-fg-2 max-w-[56px] truncate">{f.name || f.username || 'Friend'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DM list */}
        <div className="px-2 pb-[calc(env(safe-area-inset-bottom)+16px)]">
          {(() => {
            const filtered = activeDmVoids.filter(v => {
              if (!dmSearch.trim()) return true;
              return getDmDisplayName(v, v.id).toLowerCase().includes(dmSearch.toLowerCase());
            });
            const sorted = [...filtered].sort((a, b) => {
              const aMsg = msgsByKey[`${a.id}:chat`];
              const bMsg = msgsByKey[`${b.id}:chat`];
              const aTs = aMsg?.at(-1)?.createdAt ? new Date(aMsg.at(-1)!.createdAt!).getTime() : 0;
              const bTs = bMsg?.at(-1)?.createdAt ? new Date(bMsg.at(-1)!.createdAt!).getTime() : 0;
              if (aTs && bTs) return bTs - aTs;
              if (aTs) return -1;
              if (bTs) return 1;
              return getDmDisplayName(a, a.id).localeCompare(getDmDisplayName(b, b.id));
            });
            if (sorted.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-16 text-fg-3 gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10 opacity-40"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>
                  <p className="text-sm">{dmSearch ? 'No matches' : 'No direct messages yet'}</p>
                </div>
              );
            }
            return sorted.map(v => {
              const key = `${v.id}:chat`;
              const lastMsg = msgsByKey[key]?.at(-1);
              const unreadCount = unread[key] || 0;
              const hasNotif = !!notified[key];
              const displayName = getDmDisplayName(v, v.id);
              const initials = displayName.slice(0, 2).toUpperCase();
              const isActive = currentVoidId === v.id;
              // Derive peer avatar from friends list if void has no avatarUrl
              const peerId = v.id.replace(/^dm_/, '').split('_').find(id => id !== String(me?.userId));
              const peerFriend = peerId ? friendsList.find(f => String(f.id) === peerId) : null;
              const avatarSrc = v.avatarUrl || peerFriend?.avatarUrl || null;
              // Last message snippet
              let snippet = '';
              let snippetPrefix = '';
              if (lastMsg) {
                const isMe = String(lastMsg.authorId) === String(me?.userId);
                snippetPrefix = isMe ? 'You' : (lastMsg.authorName || '');
                if (lastMsg.kind === 'call_event') { snippet = 'Call ended'; snippetPrefix = ''; }
                else if (lastMsg.attachments?.length) { snippet = 'Attachment'; }
                else { snippet = lastMsg.content || ''; }
              }
              // Timestamp
              let timeLabel = '';
              if (lastMsg?.createdAt) {
                const d = new Date(lastMsg.createdAt);
                const diffMs = Date.now() - d.getTime();
                const diffMins = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMins / 60);
                const diffDays = Math.floor(diffHours / 24);
                if (diffMins < 1) timeLabel = 'now';
                else if (diffMins < 60) timeLabel = `${diffMins}m`;
                else if (diffHours < 24) timeLabel = `${diffHours}h`;
                else if (diffDays < 7) timeLabel = `${diffDays}d`;
                else timeLabel = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }
              return (
                <button
                  key={v.id}
                  className={`w-full flex items-center gap-3 rounded-2xl px-3 py-3 mb-0.5 text-left transition-colors ${isActive ? 'bg-white/10' : 'active:bg-white/8'}`}
                  onClick={() => onOpenDm(v.id)}
                >
                  {/* Avatar */}
                  <div className="relative shrink-0 h-[52px] w-[52px] rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                    {avatarSrc
                      ? <ThumbImg w={56} src={avatarSrc} alt={displayName} className="h-full w-full object-cover" />
                      : <span className="text-base font-medium text-fg-2">{initials}</span>
                    }
                  </div>
                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-[0.9375rem] font-medium truncate ${unreadCount > 0 || hasNotif ? 'text-white' : 'text-fg'}`}>{displayName}</span>
                      {timeLabel && <span className="text-xs text-fg-3 shrink-0">{timeLabel}</span>}
                    </div>
                    {(snippet || snippetPrefix) ? (
                      <p className={`text-sm truncate mt-0.5 ${unreadCount > 0 || hasNotif ? 'text-fg-2' : 'text-fg-3'}`}>
                        {snippetPrefix ? <span className="text-fg-2">{snippetPrefix}: </span> : null}{snippet}
                      </p>
                    ) : null}
                  </div>
                  {/* Unread badge */}
                  {(unreadCount > 0 || hasNotif) && (
                    <span className={`shrink-0 min-w-[20px] h-5 px-1.5 rounded-full ${tabBgClass} text-white text-meta font-medium flex items-center justify-center leading-none`}>
                      {unreadCount > 99 ? '99+' : unreadCount || '●'}
                    </span>
                  )}
                </button>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
}

export default MobileDmPage;
import ThumbImg from "../media/ThumbImg";
